import api from '../api';
import type {
  Customer,
  CustomerFilters,
  CustomersResponse,
  CustomerResponse,
  CustomerUpdateData,
  CustomerStatusUpdateData,
  CustomerCategoryUpdateData
} from '@/types/customer';

const API_BASE = '/admin/customers';

export const adminCustomerService = {
  /**
   * Get all customers with filters
   */
  async getCustomers(filters: CustomerFilters): Promise<CustomersResponse> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.brand) params.append('brand', filters.brand);

    // Ensure page and per_page are valid numbers
    const page = filters.page && filters.page > 0 ? filters.page : 1;
    const perPage = filters.per_page && filters.per_page > 0 ? filters.per_page : 10;

    params.append('page', page.toString());
    params.append('per_page', perPage.toString());

    const response = await api.get(`${API_BASE}?${params.toString()}`);
    return response.data;
  },

  /**
   * Get single customer details
   */
  async getCustomer(userId: number): Promise<CustomerResponse> {
    const response = await api.get(`${API_BASE}/${userId}`);
    return response.data;
  },

  /**
   * Update customer status (approve/reject)
   */
  async updateCustomerStatus(
    userId: number,
    data: CustomerStatusUpdateData
  ): Promise<CustomerResponse> {
    const response = await api.patch(`${API_BASE}/${userId}/status`, data);
    return response.data;
  },

  /**
   * Update customer information
   */
  async updateCustomer(
    userId: number,
    data: CustomerUpdateData
  ): Promise<CustomerResponse> {
    const response = await api.put(`${API_BASE}/${userId}`, data);
    return response.data;
  },

  /**
   * Delete customer
   */
  async deleteCustomer(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`${API_BASE}/${userId}`);
    return response.data;
  },

  /**
   * Send password reset link to customer (admin only)
   */
  async resetCustomerPassword(userId: number): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${API_BASE}/${userId}/reset-password`);
    return response.data;
  },

  /**
   * Update customer category (customer/internal)
   */
  async updateCustomerCategory(
    userId: number,
    data: CustomerCategoryUpdateData
  ): Promise<CustomerResponse> {
    const response = await api.patch(`${API_BASE}/${userId}/category`, data);
    return response.data;
  },

  /**
   * Get all customers for export (no pagination)
   */
  async getCustomersForExport(filters: CustomerFilters, noTiresOnly = false): Promise<Customer[]> {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.brand) params.append('brand', filters.brand);
    if (noTiresOnly) params.append('no_tires', '1');

    const response = await api.get(`${API_BASE}/export-data?${params.toString()}`);
    return response.data.data || [];
  }
};

